import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@art-of-hospitality-solutions-llc/ui-kit/components/tooltip';
import {
  Eye,
  EyeClosed,
  GripVertical,
  Lock,
  Pencil,
  Trash2,
  Users,
} from 'lucide-react';
import { useRef, useState } from 'react';

import { cn } from '@Utils/cn';

import type { BlockNode } from '../types';
import { hasShowForRule } from '../utils';

interface LayerChipProps {
  node: BlockNode;
  /** Resolved admin-facing label (see getBlockLabel). */
  label: string;
  icon?: React.ReactNode;
  /** Nesting depth; 0 = direct child of the region root. */
  depth?: number;
  selected?: boolean;
  /**
   * Sortable listeners/attributes spread onto the grip, making it the drag
   * handle. Undefined = not draggable (e.g. `locked.reorder`); a lock icon
   * is shown instead.
   */
  dragHandleProps?: React.HTMLAttributes<HTMLButtonElement>;
  onSelect: (id: string) => void;
  onToggleHidden: (id: string) => void;
  onDelete: (id: string) => void;
  /** Sets/clears the block's layer name (node.name). */
  onRename: (id: string, name: string | undefined) => void;
}

export default function LayerChip({
  node,
  label,
  icon,
  depth = 0,
  selected,
  dragHandleProps,
  onSelect,
  onToggleHidden,
  onDelete,
  onRename,
}: LayerChipProps) {
  const { id, hidden, locked, showFor } = node;
  const deletable = locked?.delete !== false;
  // Gated = any populated showFor axis; the app only shows this block to
  // matching members (enforced server-side).
  const gated = hasShowForRule(showFor);

  // Inline rename state is chip-local UI (like an accordion's open state);
  // the committed value flows up through onRename.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');
  // Enter/Escape close the editor themselves; this keeps the input's
  // unmount blur from double-committing (or resurrecting a cancel).
  const skipBlurRef = useRef(false);

  const startEditing = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDraft(node.name ?? '');
    skipBlurRef.current = false;
    setEditing(true);
  };

  const finishEditing = (save: boolean) => {
    if (save) onRename(id, draft.trim() ? draft : undefined);
    setEditing(false);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => onSelect(id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') onSelect(id);
      }}
      className={cn(
        'group flex w-full cursor-pointer items-center gap-2 p-2 text-left text-sm hover:bg-zinc-100',
        selected && 'bg-zinc-100',
      )}
      style={{ paddingLeft: `${8 + depth * 20}px` }}
    >
      {dragHandleProps ? (
        <button
          type="button"
          aria-label={`Drag to reorder ${label}`}
          {...dragHandleProps}
          className="shrink-0 cursor-grab touch-none text-zinc-400 active:cursor-grabbing"
        >
          <GripVertical size={16} />
        </button>
      ) : (
        <Lock className="shrink-0 text-zinc-300" size={14} />
      )}

      <div
        className={cn(
          'flex min-w-0 grow items-center gap-2',
          hidden &&
            !editing &&
            'text-zinc-400 line-through decoration-zinc-300',
        )}
      >
        {icon && <span className="shrink-0 text-zinc-500">{icon}</span>}
        {editing ? (
          <input
            autoFocus
            value={draft}
            placeholder={label}
            onChange={(e) => setDraft(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
              // Keep Enter/Space from bubbling into the chip's select.
              e.stopPropagation();
              if (e.key === 'Enter') {
                skipBlurRef.current = true;
                finishEditing(true);
              } else if (e.key === 'Escape') {
                skipBlurRef.current = true;
                finishEditing(false);
              }
            }}
            onBlur={() => {
              if (!skipBlurRef.current) finishEditing(true);
            }}
            className="w-full min-w-0 rounded border border-violet-400 bg-white px-1 py-0.5 text-sm outline-none"
          />
        ) : (
          <span className="truncate">{label}</span>
        )}
        {gated && !editing && (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="shrink-0 text-zinc-400">
                <Users size={12} />
              </span>
            </TooltipTrigger>
            <TooltipContent>
              Only shown to some members (Show For)
            </TooltipContent>
          </Tooltip>
        )}
      </div>

      {!editing && (
        <div className="flex shrink-0 items-center gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={`Rename ${label}`}
                onClick={startEditing}
                className="rounded p-0.5 text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-zinc-600"
              >
                <Pencil size={14} />
              </button>
            </TooltipTrigger>
            <TooltipContent>Edit layer name</TooltipContent>
          </Tooltip>
          {deletable && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={`Delete ${label}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete(id);
                  }}
                  className="rounded p-0.5 text-zinc-400 opacity-0 group-hover:opacity-100 hover:text-red-500"
                >
                  <Trash2 size={14} />
                </button>
              </TooltipTrigger>
              <TooltipContent>Delete layer</TooltipContent>
            </Tooltip>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                aria-label={hidden ? `Show ${label}` : `Hide ${label}`}
                onClick={(e) => {
                  e.stopPropagation();
                  onToggleHidden(id);
                }}
                className="rounded p-0.5 text-zinc-400 hover:text-zinc-600"
              >
                {hidden ? <EyeClosed size={16} /> : <Eye size={16} />}
              </button>
            </TooltipTrigger>
            <TooltipContent>
              {hidden ? 'Show layer' : 'Hide layer'}
            </TooltipContent>
          </Tooltip>
        </div>
      )}
    </div>
  );
}
